<template>
    <div class="overflow-x-hidden">
        <PageHeader :title="pageTitle">
            <template v-slot:icon>
                <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke-width="1.5" stroke="currentColor" class="page-header-icon">
                    <path stroke-linecap="round" stroke-linejoin="round" d="M6.75 3v2.25M17.25 3v2.25M3 18.75V7.5a2.25 2.25 0 0 1 2.25-2.25h13.5A2.25 2.25 0 0 1 21 7.5v11.25m-18 0A2.25 2.25 0 0 0 5.25 21h13.5A2.25 2.25 0 0 0 21 18.75m-18 0v-7.5A2.25 2.25 0 0 1 5.25 9h13.5A2.25 2.25 0 0 1 21 11.25v7.5" />
                </svg>
            </template>
        </PageHeader>

        <div class="bg-white rounded-lg shadow-sm p-3 mb-4">
            <div class="flex flex-wrap gap-3 mb-3">
                <BaseButton skin="primary" @click="openCreateModal">
                    <template v-slot:icon>
                        <svg xmlns="http://www.w3.org/2000/svg" class="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 6v6m0 0v6m0-6h6m-6 0H6" />
                        </svg>
                    </template>
                    Adicionar
                </BaseButton>
                <BaseButton :to="{ name: 'trainingTypes' }" skin="primary-outline">Tipos de Treinamento</BaseButton>
                <BaseButton :to="{ name: 'certificateTemplates' }" skin="primary-outline">Modelos de Certificado</BaseButton>
            </div>

            <div class="grid grid-cols-4 gap-x-4 gap-y-2 items-end">
                <div class="col-span-2">
                    <BaseInput label="Buscar" placeholder="Buscar por tema ou instrutor" v-model="filter.search" @input="onFilterTyping" />
                </div>
                <div class="flex flex-col">
                    <label class="label">De</label>
                    <Datepicker locale="pt-BR" :enableTimePicker="false" format="dd/MM/yyyy" v-model="filter.start_date" autoApply @update:modelValue="loadData" />
                </div>
                <div class="flex flex-col">
                    <label class="label">Até</label>
                    <Datepicker locale="pt-BR" :enableTimePicker="false" format="dd/MM/yyyy" v-model="filter.end_date" autoApply @update:modelValue="loadData" />
                </div>
                <div class="flex items-center gap-2 pt-1">
                    <button type="button" @click="toggleShowInactive" :class="showInactive ? 'bg-green-600' : 'bg-gray-300'" class="relative inline-flex h-5 w-10 flex-shrink-0 cursor-pointer rounded-full transition-colors duration-200 focus:outline-none">
                        <span :class="showInactive ? 'translate-x-5' : 'translate-x-0'" class="inline-block h-5 w-5 transform rounded-full bg-white shadow transition duration-200"></span>
                    </button>
                    <span class="text-sm text-gray-600">Mostrar inativos</span>
                </div>
            </div>
        </div>

        <!-- Loading Skeleton -->
        <div v-if="loading" class="table-container">
            <table class="w-full eventos-table">
                <thead>
                    <tr>
                        <th></th>
                        <th>Tema</th>
                        <th>Instrutor</th>
                        <th>Tipo</th>
                        <th>N. Part.</th>
                        <th>Certificados</th>
                        <th>Ações</th>
                    </tr>
                </thead>
                <tbody>
                    <tr v-for="item in 5" :key="`event-skeleton-${item}`" class="table-row">
                        <td v-for="cell in 7" :key="`event-skeleton-cell-${cell}`">
                            <div class="h-3 rounded bg-gray-200 animate-pulse"></div>
                        </td>
                    </tr>
                </tbody>
            </table>
        </div>

        <Alert text="Não existem eventos para esse filtro" :show="!events.length && !loading" />

        <div class="table-container" v-if="events.length && !loading">
            <table class="w-full eventos-table">
                <thead>
                    <tr>
                        <th></th>
                        <th>Tema</th>
                        <th>Instrutor</th>
                        <th>Tipo</th>
                        <th>N. Part.</th>
                        <th>Certificados</th>
                        <th>Ações</th>
                    </tr>
                </thead>
                <tbody>
                    <tr class="table-row cursor-pointer hover:bg-green-50 transition-opacity duration-300" :class="{ 'opacity-30 pointer-events-none': deletingEvent[ev.uuid] }" v-for="ev in events" :key="ev.uuid" @click="goToEvent(ev.uuid)">
                        <td class="text-left text-sm leading-5">
                            <router-link :to="{ name: 'evento', params: { uuid: ev.uuid } }" title="Ver" class="cursor-pointer text-green-600 hover:text-green-800" @click.stop>
                                <svg xmlns="http://www.w3.org/2000/svg" class="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor" stroke-width="1.5">
                                    <path stroke-linecap="round" stroke-linejoin="round" d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                                    <path stroke-linecap="round" stroke-linejoin="round" d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z" />
                                </svg>
                            </router-link>
                        </td>
                        <td>
                            <span class="text-sm leading-5 text-green-900 font-semibold">{{ ev.theme }}</span>
                            <span v-if="ev.is_deleted" class="ml-2 text-xs bg-red-100 text-red-600 px-1.5 py-0.5 rounded">inativo</span>
                            <div class="text-xs text-gray-500 mt-0.5">{{ formatEventDate(ev.event_date) }}</div>
                            <div v-if="ev.event_date" class="text-xs text-gray-400">{{ relativeTime(ev.event_date) }}</div>
                        </td>
                        <td>
                            <span class="text-sm leading-5 text-green-700">{{ ev.instructor || ' -- ' }}</span>
                        </td>
                        <td>
                            <span class="text-sm leading-5 text-green-700">{{ ev.training_type?.name || ' -- ' }}</span>
                        </td>
                        <td>
                            <span class="text-sm leading-5 text-green-900">{{ ev.participant_count ?? 0 }}</span>
                        </td>
                        <td>
                            <span class="text-sm leading-5 text-green-900">{{ ev.certificate_sent_count ?? 0 }}/{{ ev.participant_count ?? 0 }}</span>
                        </td>
                        <td @click.stop>
                            <div class="flex items-center gap-x-4">
                                <span v-if="!ev.is_deleted" title="Copiar link de inscrição" class="cursor-pointer text-green-600 hover:text-green-800" @click="copyRegistrationLink(ev.uuid)">
                                    <svg xmlns="http://www.w3.org/2000/svg" class="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor" stroke-width="1.5">
                                        <path stroke-linecap="round" stroke-linejoin="round" d="M13.5 4.5H8.25A2.25 2.25 0 0 0 6 6.75v10.5A2.25 2.25 0 0 0 8.25 19.5h7.5A2.25 2.25 0 0 0 18 17.25V9M13.5 4.5 18 9m-4.5-4.5V9H18" />
                                    </svg>
                                </span>
                                <span v-if="!ev.is_deleted" title="Resumo" class="cursor-pointer text-green-600 hover:text-green-800" @click="downloadEventInsights(ev)">
                                    <svg v-if="!insightsLoading[ev.uuid]" xmlns="http://www.w3.org/2000/svg" class="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor" stroke-width="1.5">
                                        <path stroke-linecap="round" stroke-linejoin="round" d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                                    </svg>
                                    <svg v-else class="animate-spin h-6 w-6" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                                        <circle class="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" stroke-width="4"></circle>
                                        <path class="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z"></path>
                                    </svg>
                                </span>
                                <span v-if="!ev.is_deleted" title="Excluir" class="cursor-pointer text-red-400 hover:text-red-600" @click="deleteEvent(ev)">
                                    <svg xmlns="http://www.w3.org/2000/svg" class="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor" stroke-width="1.5">
                                        <path stroke-linecap="round" stroke-linejoin="round" d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                                    </svg>
                                </span>
                                <span v-if="ev.is_deleted" title="Restaurar" class="cursor-pointer text-green-600 hover:text-green-800" @click="restoreEvent(ev)">
                                    <svg xmlns="http://www.w3.org/2000/svg" class="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor" stroke-width="1.5">
                                        <path stroke-linecap="round" stroke-linejoin="round" d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
                                    </svg>
                                </span>
                            </div>
                        </td>
                    </tr>
                </tbody>
            </table>
            <Pagination :total="totalEvents" :limit="limit" @changePage="changePagination" />
        </div>

        <!-- CREATE EVENT MODAL -->
        <ModalV2 ref="createModalRef" :hide="true" title="Novo evento" size="lg">
            <template v-slot:body>
                <div class="grid grid-cols-2 gap-x-8 gap-y-4">
                    <BaseInput label="Tema" placeholder="Tema do treinamento" v-model="form.theme" required="true" :isInvalid="errors.theme">
                        <template v-slot:tip>
                            <span v-if="errors.theme" class="text-xs text-red-500">Informe o tema</span>
                        </template>
                    </BaseInput>
                    <BaseInput label="Instrutor" placeholder="Nome do instrutor" v-model="form.instructor" required="true" :isInvalid="errors.instructor">
                        <template v-slot:tip>
                            <span v-if="errors.instructor" class="text-xs text-red-500">Informe o instrutor</span>
                        </template>
                    </BaseInput>
                    <div class="flex flex-col">
                        <label class="label">Data e hora</label>
                        <Datepicker locale="pt-BR" format="dd/MM/yyyy HH:mm" v-model="form.event_date" autoApply />
                    </div>
                    <BaseInput type="number" label="Carga horária (horas)" placeholder="Ex: 8" v-model="form.workload_hours" required="true" :isInvalid="errors.workload_hours">
                        <template v-slot:tip>
                            <span v-if="errors.workload_hours" class="text-xs text-red-500">Informe a carga horária</span>
                        </template>
                    </BaseInput>
                    <BaseSelect label="Tipo de treinamento" emptyOption="-- Selecione --" :options="trainingTypeOptions" v-model="form.training_type_id" :disabled="loadingOptions" @change="onSelectTrainingType">
                        <template v-slot:help>
                            <svg v-if="loadingOptions" class="animate-spin ml-2 h-4 w-4 text-green-600" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                                <circle class="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" stroke-width="4"></circle>
                                <path class="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z"></path>
                            </svg>
                        </template>
                    </BaseSelect>
                    <BaseSelect label="Formulário do evento" emptyOption="-- Selecione --" :options="formOptions" v-model="form.form_id" :disabled="loadingOptions">
                        <template v-slot:help>
                            <svg v-if="loadingOptions" class="animate-spin ml-2 h-4 w-4 text-green-600" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                                <circle class="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" stroke-width="4"></circle>
                                <path class="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z"></path>
                            </svg>
                        </template>
                    </BaseSelect>
                    <div class="flex items-end">
                        <div class="rounded border border-gray-200 px-4 py-3 w-full">
                            <BaseCheckbox id="emits-certificate-modal" label="Emite certificado" v-model="form.emits_certificate" />
                        </div>
                    </div>
                    <BaseSelect v-if="form.emits_certificate" label="Modelo de certificado" required="true" emptyOption="-- Selecione --" :options="certificateTemplateOptions" v-model="form.certificate_template_id" :disabled="loadingOptions" :isInvalid="errors.certificate_template_id">
                        <template v-slot:help>
                            <svg v-if="loadingOptions" class="animate-spin ml-2 h-4 w-4 text-green-600" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                                <circle class="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" stroke-width="4"></circle>
                                <path class="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z"></path>
                            </svg>
                        </template>
                        <template v-slot:tip>
                            <span v-if="errors.certificate_template_id" class="text-xs text-red-500">Selecione o modelo de certificado</span>
                        </template>
                    </BaseSelect>
                </div>
            </template>
            <template v-slot:footer>
                <BaseButton skin="primary-outline" @click="closeCreateModal">Fechar</BaseButton>
                <BaseButton skin="primary" :disabled="loadingCreate" @click="submitCreate">
                    <svg v-if="loadingCreate" class="animate-spin ml-1 mr-3 h-5 w-5 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                        <circle class="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" stroke-width="4"></circle>
                        <path class="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                    </svg>
                    Salvar
                </BaseButton>
            </template>
        </ModalV2>
    </div>
</template>

<script>

import { ref, watch } from 'vue';
import { useRouter } from 'vue-router';
import { formatDistanceToNow } from 'date-fns';
import { ptBR } from 'date-fns/locale';
import { EventService, TrainingTypeService, CertificateTemplateService, FormService } from '../services/Entity';
import Alert from '@/components/Alert';
import Pagination from '@/components/Pagination';
import ModalV2 from '@/components/ModalV2';
import Swal from 'sweetalert';
import { toast } from 'vue3-toastify';
import 'vue3-toastify/dist/index.css';
import { dateTime } from '../filters/date';

export default {
    name: 'Eventos',
    components: {
        Alert,
        Pagination,
        ModalV2
    },
    setup() {
        const pageTitle = ref('Eventos e Treinamentos');
        const router = useRouter();
        const events = ref([]);
        const totalEvents = ref(0);
        const page = ref(1);
        const limit = ref(20);
        const loading = ref(false);
        const insightsLoading = ref({});
        const deletingEvent = ref({});
        const showInactive = ref(localStorage.getItem('eventos_show_inactive') === 'true');
        const today = new Date();
        const defaultStartDate = new Date(today.getFullYear(), today.getMonth() - 1, 1);
        const defaultEndDate = new Date(today.getFullYear(), today.getMonth() + 2, 0, 23, 59, 59);
        const filter = ref({
            search: '',
            start_date: defaultStartDate,
            end_date: defaultEndDate
        });
        let filterTypingTimeout = null;

        const createModalRef = ref();
        const loadingCreate = ref(false);
        const loadingOptions = ref(false);
        const optionsLoaded = ref(false);
        const trainingTypes = ref([]);
        const trainingTypeOptions = ref([]);
        const certificateTemplateOptions = ref([]);
        const formOptions = ref([]);
        const emptyForm = () => ({
            theme: '',
            instructor: '',
            event_date: null,
            workload_hours: '',
            training_type_id: '',
            form_id: '',
            certificate_template_id: '',
            emits_certificate: true
        });
        const form = ref(emptyForm());
        const errors = ref({ theme: false, instructor: false, workload_hours: false, certificate_template_id: false });

        function resetErrors() {
            errors.value = { theme: false, instructor: false, workload_hours: false, certificate_template_id: false };
        }

        watch(() => form.value.theme, value => { if (value && value.trim()) errors.value.theme = false; });
        watch(() => form.value.instructor, value => { if (value && value.trim()) errors.value.instructor = false; });
        watch(() => form.value.workload_hours, value => { if (value !== '' && value != null && Number(value) > 0) errors.value.workload_hours = false; });
        watch(() => form.value.certificate_template_id, value => { if (value) errors.value.certificate_template_id = false; });
        watch(() => form.value.emits_certificate, value => { if (!value) errors.value.certificate_template_id = false; });

        async function loadTrainingTypes() {
            try {
                const { data: { items } } = await TrainingTypeService.list(1, 9999);
                trainingTypes.value = items;
                trainingTypeOptions.value = items.map(item => ({ title: item.name, value: item.id }));
            } catch (err) {
                console.log(err);
            }
        }

        async function loadCertificateTemplates() {
            try {
                const { data: { items } } = await CertificateTemplateService.list(1, 9999);
                certificateTemplateOptions.value = items.map(item => ({ title: item.name, value: item.id }));
            } catch (err) {
                console.log(err);
            }
        }

        async function loadForms() {
            try {
                const { data: { items } } = await FormService.list(1, 9999, { type: 'event' });
                const eventForms = (items || []).filter(item => (item.type || item.form_body?.type) === 'event');
                formOptions.value = eventForms.map(item => ({ title: item.name, value: item.id }));
            } catch (err) {
                console.log(err);
            }
        }

        function onSelectTrainingType() {
            const selected = trainingTypes.value.find(item => String(item.id) === String(form.value.training_type_id));
            if (!selected) {
                form.value.emits_certificate = false;
                form.value.certificate_template_id = '';
                return;
            }
            form.value.emits_certificate = !!selected.generates_certificate;
            form.value.certificate_template_id = selected.certificate_template?.id ?? selected.certificate_template_id ?? '';
        }

        async function loadOptions() {
            if (optionsLoaded.value || loadingOptions.value) return;
            loadingOptions.value = true;
            try {
                await Promise.all([loadTrainingTypes(), loadCertificateTemplates(), loadForms()]);
                optionsLoaded.value = true;
            } finally {
                loadingOptions.value = false;
            }
        }

        function openCreateModal() {
            form.value = emptyForm();
            resetErrors();
            createModalRef.value.setModalStatus('open');
            loadOptions();
        }

        function closeCreateModal() {
            createModalRef.value.setModalStatus('close');
        }

        function buildCreatePayload() {
            return {
                theme: form.value.theme,
                instructor: form.value.instructor,
                event_date: form.value.event_date,
                workload_hours: form.value.workload_hours,
                training_type_id: form.value.training_type_id || null,
                form_id: form.value.form_id || null,
                certificate_template_id: form.value.certificate_template_id || null,
                emits_certificate: !!form.value.emits_certificate
            };
        }

        async function submitCreate() {
            resetErrors();
            let valid = true;

            if (!form.value.theme || !form.value.theme.trim()) {
                errors.value.theme = true;
                valid = false;
            }

            if (!form.value.instructor || !form.value.instructor.trim()) {
                errors.value.instructor = true;
                valid = false;
            }

            if (form.value.workload_hours === '' || form.value.workload_hours == null || Number(form.value.workload_hours) <= 0) {
                errors.value.workload_hours = true;
                valid = false;
            }

            if (form.value.emits_certificate && !form.value.certificate_template_id) {
                errors.value.certificate_template_id = true;
                valid = false;
            }

            if (!valid) return;

            try {
                loadingCreate.value = true;
                await EventService.create(buildCreatePayload());
                closeCreateModal();
                toast('Evento criado com sucesso!', { type: 'success', autoClose: 2500 });
                await loadData();
            } catch (err) {
                console.log(err);
                Swal({
                    title: 'Ocorreu um erro ao salvar o evento',
                    text: 'Atualize a página e tente novamente',
                    icon: 'error'
                });
            } finally {
                loadingCreate.value = false;
            }
        }

        function buildFilter() {
            const payload = {};
            if (filter.value.search?.trim()) payload.search = filter.value.search.trim();
            if (filter.value.start_date) payload.start_date = filter.value.start_date;
            if (filter.value.end_date) payload.end_date = filter.value.end_date;
            if (showInactive.value) payload.include_inactive = true;

            return payload;
        }

        function toggleShowInactive() {
            showInactive.value = !showInactive.value;
            localStorage.setItem('eventos_show_inactive', showInactive.value);
            page.value = 1;
            loadData();
        }

        async function restoreEvent(ev) {
            try {
                deletingEvent.value[ev.uuid] = true;
                await EventService.restore(ev.uuid);
                toast('Evento restaurado!', { type: 'success', autoClose: 2500 });
                await loadData();
            } catch (err) {
                console.log(err);
                deletingEvent.value[ev.uuid] = false;
                Swal({ title: 'Erro ao restaurar o evento', text: 'Tente novamente', icon: 'error' });
            }
        }

        async function deleteEvent(ev) {
            const confirmed = await Swal({
                title: 'Inativar evento?',
                text: `"${ev.theme}" ficará inativo e não aparecerá na lista.`,
                icon: 'warning',
                buttons: ['Cancelar', 'Inativar'],
                dangerMode: true
            });

            if (!confirmed) return;

            try {
                deletingEvent.value[ev.uuid] = true;
                await EventService.destroy(ev.uuid);
                toast('Evento inativado!', { type: 'success', autoClose: 2500 });
                await loadData();
            } catch (err) {
                console.log(err);
                deletingEvent.value[ev.uuid] = false;
                Swal({ title: 'Erro ao inativar o evento', text: 'Tente novamente', icon: 'error' });
            }
        }

        async function loadData() {
            loading.value = true;
            await loadEvents();
            loading.value = false;
        }

        async function loadEvents() {
            try {
                const { data: { total, items } } = await EventService.list(page.value, limit.value, buildFilter());

                events.value = items;
                totalEvents.value = total;
            } catch (err) {
                console.log(err);
                Swal({
                    title: 'Ocorreu um erro ao obter dados dos eventos',
                    text: 'Atualize a página e tente novamente',
                    icon: 'error'
                });
            }
        }

        function onFilterTyping() {
            page.value = 1;
            if (filterTypingTimeout) clearTimeout(filterTypingTimeout);
            filterTypingTimeout = setTimeout(() => {
                loadData();
                filterTypingTimeout = null;
            }, 400);
        }

        async function changePagination(currentPage) {
            page.value = currentPage;
            await loadData();
        }

        function goToEvent(uuid) {
            router.push({ name: 'evento', params: { uuid } });
        }

        function buildRegistrationLink(uuid) {
            return `${window.location.origin}/inscricao/${uuid}`;
        }

        async function copyRegistrationLink(uuid) {
            try {
                await navigator.clipboard.writeText(buildRegistrationLink(uuid));
                toast('Link de inscrição copiado!', {
                    type: 'success',
                    autoClose: 2500,
                    position: 'top-right'
                });
            } catch (err) {
                console.log(err);
                Swal({
                    title: 'Não foi possível copiar o link',
                    text: 'Tente novamente',
                    icon: 'warning'
                });
            }
        }

        function formatEventDate(value) {
            if (!value) return ' -- ';
            return dateTime(value);
        }

        function relativeTime(value) {
            if (!value) return '';
            try {
                return formatDistanceToNow(new Date(value), { addSuffix: true, locale: ptBR });
            } catch (e) {
                return '';
            }
        }

        function triggerBlobDownload(response, fallbackName) {
            const blob = new Blob([response.data], { type: 'application/pdf' });
            const url = window.URL.createObjectURL(blob);
            let filename = fallbackName;
            const cd = response.headers && (response.headers['content-disposition'] || response.headers['Content-Disposition']);
            if (cd) {
                const match = /filename="?([^"]+)"?/.exec(cd);
                if (match) filename = match[1];
            }
            const link = document.createElement('a');
            link.href = url;
            link.download = filename;
            document.body.appendChild(link);
            link.click();
            link.remove();
            window.URL.revokeObjectURL(url);
        }

        async function downloadEventInsights(ev) {
            try {
                insightsLoading.value[ev.uuid] = true;
                const response = await EventService.downloadInsights(ev.uuid);
                triggerBlobDownload(response, `insights-evento-${ev.uuid}.pdf`);
            } catch (err) {
                console.log(err);
                Swal({ title: 'Erro ao gerar o PDF de insights', text: 'Tente novamente', icon: 'error' });
            } finally {
                insightsLoading.value[ev.uuid] = false;
            }
        }

        loadData();

        return {
            pageTitle,
            events,
            totalEvents,
            page,
            limit,
            loading,
            filter,
            loadData,
            onFilterTyping,
            changePagination,
            goToEvent,
            copyRegistrationLink,
            formatEventDate,
            relativeTime,
            insightsLoading,
            downloadEventInsights,
            deletingEvent,
            showInactive,
            toggleShowInactive,
            deleteEvent,
            restoreEvent,
            createModalRef,
            loadingCreate,
            loadingOptions,
            form,
            errors,
            trainingTypeOptions,
            certificateTemplateOptions,
            formOptions,
            onSelectTrainingType,
            openCreateModal,
            closeCreateModal,
            submitCreate
        };
    }
}
</script>

<style>
.eventos-table .table-row > td {
    white-space: normal;
    word-break: break-word;
}
</style>
